/** @jsx jsx */
/** @jsxFrag React.Fragment */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useState } from 'react';

import useFluxibleStore from 'js/lib/fluxibleHooks/useFluxibleStore';
import user from 'js/lib/user';

import { Button, InlineNotification, Typography2, breakpoints } from '@coursera/cds-core';

import type {
  LinkedAccount,
  NaptimeData,
} from 'bundles/account-settings/components/sections/delete-account/AccountDeletion.types';
import AccountDeletionModal from 'bundles/account-settings/components/sections/delete-account/AccountDeletionModal';
import { getLinkedAccountErrorMessage } from 'bundles/account-settings/components/sections/delete-account/helpers';
import { useNaptime } from 'bundles/naptimejs';
/* @ts-expect-error ts-migrate(7016) FIXME: Untyped import */
import DegreeLearnerMembershipsV1 from 'bundles/naptimejs/resources/degreeLearnerMemberships.v1';
import AccountLinkStore from 'bundles/third-party-auth/stores/AccountLinkStore';

import _t from 'i18n!nls/account-settings';

type Props = {
  userId?: number;
};

const styles = {
  container: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-200);
  `,
  row: css`
    display: flex;
    gap: var(--cds-spacing-200);
    align-items: center;

    ${breakpoints.down('xs')} {
      flex-direction: column;
      align-items: stretch;
    }
  `,
  description: css`
    flex: 1;

    && {
      margin: 0;
    }
  `,
  button: css`
    flex-shrink: 0;
    min-width: 140px;

    ${breakpoints.down('xs')} {
      width: 100%;
      max-width: unset;
    }
  `,
  linkedAccountAlert: css`
    &:empty {
      display: none;
    }
  `,
  headingGroup: css`
    display: flex;
    flex-direction: column;
    gap: var(--cds-spacing-50);
  `,
  heading: css`
    && {
      margin: 0;
    }
  `,
};

const AccountDeletion = ({ userId }: Props) => {
  const [showAccountDeletionModal, setShowAccountDeletionModal] = useState(false);
  const [linkedAccount, setLinkedAccount] = useState<LinkedAccount | null>(null);

  const linked = useFluxibleStore(AccountLinkStore, (store) => store.linked);

  const {
    data: { degreeMemberships },
  } = useNaptime<NaptimeData>(() => ({
    degreeMemberships: DegreeLearnerMembershipsV1.finder('byUser', {
      params: {
        userId: user.get().id,
      },
      fields: ['degreeId'],
    }),
  }));

  const handleOpenModal = () => {
    if (linked?.facebook) {
      setLinkedAccount('facebook');
    } else if (linked?.apple) {
      setLinkedAccount('apple');
    } else if (linked?.google) {
      setLinkedAccount('google');
    } else {
      setLinkedAccount(null);
      setShowAccountDeletionModal(true);
    }
  };

  const degreeMembershipLength = degreeMemberships?.length ?? 0;

  // Partner guidelines say that degree learners should not be allowed to delete their account.
  if (degreeMembershipLength > 0) {
    return (
      <div css={[styles.container, styles.headingGroup]}>
        <Typography2 variant="subtitleLarge" component="h2" css={styles.heading}>
          {_t('Delete account')}
        </Typography2>
        <Typography2 variant="bodySecondary" component="p" color="supportText" css={styles.description}>
          {_t(
            `In accordance with higher education regulations, the delete function has been disabled for learners in credit-eligible and for-credit offerings.`
          )}
        </Typography2>
      </div>
    );
  }

  return (
    <div css={styles.container}>
      {showAccountDeletionModal && userId !== undefined && (
        <AccountDeletionModal
          userId={userId}
          onClose={() => {
            setShowAccountDeletionModal(false);
          }}
        />
      )}
      <div css={styles.row}>
        <div css={styles.headingGroup}>
          <Typography2 variant="subtitleLarge" component="h2" color="error" css={styles.heading}>
            {_t('Delete account')}
          </Typography2>
          <Typography2 variant="bodySecondary" component="p" color="supportText" css={styles.description}>
            {_t(
              `
              If you delete your account, your personal information will be wiped from Coursera's servers,
              all of your course activity will be anonymized and any certificates earned will be deleted.
              `
            )}
            <Typography2 variant="bodySecondary" component="span" color="error">
              {_t('This action cannot be undone!')}
            </Typography2>
          </Typography2>
        </div>
        <Button type="button" variant="secondary" onClick={handleOpenModal} css={styles.button}>
          {_t('Delete account')}
        </Button>
      </div>
      <div role="alert" aria-atomic="true" css={styles.linkedAccountAlert}>
        {linkedAccount && (
          <InlineNotification severity="error">{getLinkedAccountErrorMessage(linkedAccount)}</InlineNotification>
        )}
      </div>
      <InlineNotification severity="warning">
        {_t('Cancel any active subscriptions before you delete your account.')}
      </InlineNotification>
    </div>
  );
};

export default AccountDeletion;
